import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { 
  Building, 
  Mail, 
  Lock, 
  User, 
  CheckCircle, 
  ArrowRight,
  Heart,
  Shield,
  Sparkles
} from 'lucide-react'

import { useAuth } from '../../hooks/useAuth'
import { db } from '../../lib/supabase'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Card } from '../../components/ui/Card'

const registerSchema = z.object({
  companyName: z.string().min(2, 'Company name must be at least 2 characters'),
  industry: z.string().optional(),
  email: z.string().email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
})

type RegisterFormData = z.infer<typeof registerSchema>

export const Register: React.FC = () => {
  const { t } = useTranslation()
  const { signUp } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [step, setStep] = useState(1)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    trigger
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
  })

  const watchedFields = watch()

  const nextStep = async () => {
    const fieldsToValidate = step === 1 
      ? ['companyName', 'industry'] 
      : ['email', 'password', 'confirmPassword']
    
    const isValid = await trigger(fieldsToValidate as any)
    if (isValid) {
      setStep(step + 1)
    }
  }

  const prevStep = () => {
    setStep(step - 1)
  }

  const onSubmit = async (data: RegisterFormData) => {
    setLoading(true)
    try {
      console.log('🚀 Starting registration process...')
      
      // Step 1: Create the company first
      console.log('🏢 Creating company...')
      const { data: company, error: companyError } = await db.companies.create({
        name: data.companyName,
        industry: data.industry || null,
      })

      if (companyError) {
        console.error('❌ Company creation error:', companyError)
        toast.error('Failed to create company. Please try again.')
        return
      }

      if (!company?.id) {
        console.error('❌ No company ID returned')
        toast.error('Failed to create company: No company data received')
        return
      }

      console.log('✅ Company created successfully:', company.id)

      // Step 2: Sign up the user with metadata
      console.log('📝 Signing up user...')
      const { data: authData, error: signUpError } = await signUp(data.email, data.password, {
        company_name: data.companyName,
        industry: data.industry,
        role: 'HR',
        company_id: company.id,
      })

      if (signUpError) {
        console.error('❌ SignUp error:', signUpError)
        
        // Handle specific signup errors
        if (signUpError.message?.includes('User already registered') || 
            signUpError.message?.includes('user_already_exists')) {
          toast.error('An account with this email already exists. Please sign in instead.')
          return
        } else if (signUpError.message?.includes('Invalid email')) {
          toast.error('Please enter a valid email address.')
          return
        } else if (signUpError.message?.includes('Password')) {
          toast.error('Password must be at least 8 characters long.')
          return
        } else if (signUpError.message?.includes('Email rate limit exceeded')) {
          toast.error('Too many signup attempts. Please wait a few minutes before trying again.')
          return
        } else {
          console.error('Unexpected signup error:', signUpError)
          toast.error(`Registration failed: ${signUpError.message || 'Unknown error'}`)
          return
        }
      }

      if (!authData?.user) {
        console.error('❌ No user returned from signup')
        toast.error('Registration failed: No user data received')
        return
      }

      console.log('✅ User signed up successfully:', authData.user.id)

      // Step 3: Wait for auth user to be fully created
      console.log('⏳ Waiting for auth user to be fully created...')
      await new Promise(resolve => setTimeout(resolve, 2000))

      // Step 4: Create the user record with retry logic
      console.log('👤 Creating user record...')
      let userRecord = null
      let userError = null
      let retryCount = 0
      const maxRetries = 5

      while (retryCount < maxRetries && !userRecord) {
        try {
          const { data: userData, error: createUserError } = await db.users.create({
            id: authData.user.id,
            email: data.email,
            role: 'HR',
            company_id: company.id,
            status: 'active',
          })

          if (createUserError) {
            userError = createUserError
            console.error(`❌ User record creation error (attempt ${retryCount + 1}):`, createUserError)
            
            if (retryCount < maxRetries - 1) {
              console.log('⏳ Retrying user creation in 2 seconds...')
              await new Promise(resolve => setTimeout(resolve, 2000))
            }
          } else {
            userRecord = userData
            console.log('✅ User record created successfully')
          }
        } catch (error) {
          console.error(`❌ User creation attempt ${retryCount + 1} failed:`, error)
          userError = error
          
          if (retryCount < maxRetries - 1) {
            console.log('⏳ Retrying user creation in 2 seconds...')
            await new Promise(resolve => setTimeout(resolve, 2000))
          }
        }
        
        retryCount++
      }

      if (!userRecord && userError) {
        console.error('❌ Failed to create user record after all retries:', userError)
        toast.error('Account setup incomplete. Please try signing in or contact support.')
        // Still navigate to dashboard as auth user exists
        navigate('/dashboard')
        return
      }

      console.log('🎉 Registration completed successfully!')
      toast.success('🎉 Welcome to Mentra! Your account has been created successfully.')
      navigate('/dashboard')
      
    } catch (error: any) {
      console.error('❌ Unexpected registration error:', error)
      
      // Provide specific error messages
      if (error.message?.includes('company')) {
        toast.error('Failed to create company. Please try again.')
      } else if (error.message?.includes('user')) {
        toast.error('Failed to create user account. Please try again.')
      } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
        toast.error('Network error. Please check your connection and try again.')
      } else {
        toast.error('An unexpected error occurred. Please try again.')
      }
    } finally {
      setLoading(false)
    }
  }

  const benefits = [
    { icon: Shield, text: "HIPAA-compliant security" },
    { icon: Heart, text: "Empathetic AI insights" },
    { icon: CheckCircle, text: "14-day free trial" },
    { icon: Sparkles, text: "Setup in 5 minutes" }
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 via-white to-lavender-50 flex items-center justify-center p-4">
      <div className="w-full max-w-6xl grid lg:grid-cols-2 gap-8 items-center">
        {/* Left Side - Benefits */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="hidden lg:block"
        >
          <div className="text-center lg:text-left">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <div className="flex items-center justify-center lg:justify-start mb-6">
                <div className="w-12 h-12 bg-sage-500 rounded-2xl flex items-center justify-center mr-4">
                  <Heart className="w-7 h-7 text-white" />
                </div>
                <h1 className="text-3xl font-bold text-sage-800">Mentra</h1>
              </div>
              
              <h2 className="text-4xl font-bold text-sage-900 mb-4">
                Create Your Team's
                <span className="text-sage-600 block">Wellness Sanctuary</span>
              </h2>
              
              <p className="text-xl text-sage-600 mb-8 leading-relaxed">
                Join thousands of companies building healthier, more empathetic workplaces through AI-powered wellness insights.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="space-y-4 mb-8"
            >
              {benefits.map((benefit, index) => (
                <motion.div
                  key={benefit.text}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 + index * 0.1 }}
                  className="flex items-center"
                >
                  <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center mr-4">
                    <benefit.icon className="w-5 h-5 text-sage-600" />
                  </div>
                  <span className="text-sage-700 font-medium">{benefit.text}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.8 }}
              className="bg-gradient-to-r from-sage-500 to-emerald-600 rounded-2xl p-6 text-white"
            >
              <h3 className="font-semibold mb-2">Trusted by 1,000+ Companies</h3>
              <p className="text-sage-100 text-sm">
                From startups to enterprises, teams worldwide trust Mentra for their mental health initiatives.
              </p>
            </motion.div>
          </div>
        </motion.div>

        {/* Right Side - Registration Form */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-md mx-auto lg:mx-0"
        >
          {/* Mobile Header */}
          <div className="lg:hidden text-center mb-8">
            <div className="flex items-center justify-center mb-4">
              <div className="w-10 h-10 bg-sage-500 rounded-xl flex items-center justify-center mr-3">
                <Heart className="w-6 h-6 text-white" />
              </div>
              <h1 className="text-2xl font-bold text-sage-800">Mentra</h1>
            </div>
            <p className="text-sage-600">Create your team's wellness sanctuary</p>
          </div>

          <Card className="p-8 shadow-2xl border-2 border-sage-100 bg-white/90 backdrop-blur-sm">
            {/* Progress Indicator */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm font-medium text-sage-600">
                  Step {step} of 2
                </span>
                <span className="text-sm text-sage-500">
                  {step === 1 ? 'Company Info' : 'Account Setup'}
                </span>
              </div>
              <div className="w-full bg-sage-200 rounded-full h-2">
                <motion.div
                  className="bg-gradient-to-r from-sage-500 to-emerald-600 h-2 rounded-full"
                  initial={{ width: '50%' }}
                  animate={{ width: step === 1 ? '50%' : '100%' }}
                  transition={{ duration: 0.3 }}
                />
              </div>
            </div>

            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-sage-800 mb-2">
                {step === 1 ? 'Company Information' : 'Create Your Account'}
              </h2>
              <p className="text-sage-600 text-sm">
                {step === 1 
                  ? 'Tell us about your organization'
                  : 'Set up your HR admin account'
                }
              </p>
              <p className="text-sage-600 text-sm mt-2">
                Already have an account?{' '}
                <Link
                  to="/auth/login"
                  className="text-sage-600 hover:text-sage-700 font-medium underline"
                >
                  Sign in here
                </Link>
              </p>
            </div>

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              {step === 1 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <div className="relative">
                    <Building className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
                    <Input
                      label="Company Name *"
                      {...register('companyName')}
                      error={errors.companyName?.message}
                      placeholder="Acme Corporation"
                      className="pl-11 border-sage-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                  </div>

                  <div className="relative">
                    <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
                    <Input
                      label="Industry (Optional)"
                      {...register('industry')}
                      error={errors.industry?.message}
                      placeholder="Technology, Healthcare, Finance..."
                      className="pl-11 border-sage-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                  </div>

                  <Button
                    type="button"
                    onClick={nextStep}
                    className="w-full bg-gradient-to-r from-sage-500 to-emerald-600 hover:from-sage-600 hover:to-emerald-700"
                    size="lg"
                  >
                    Continue
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </motion.div>
              )}

              {step === 2 && (
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  className="space-y-4"
                >
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
                    <Input
                      label="Work Email *"
                      type="email"
                      {...register('email')}
                      error={errors.email?.message}
                      placeholder="hr@company.com"
                      className="pl-11 border-sage-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
                    <Input
                      label="Password *"
                      type="password"
                      {...register('password')}
                      error={errors.password?.message}
                      placeholder="••••••••"
                      className="pl-11 border-sage-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                  </div>

                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-sage-400 w-5 h-5" />
                    <Input
                      label="Confirm Password *"
                      type="password"
                      {...register('confirmPassword')}
                      error={errors.confirmPassword?.message}
                      placeholder="••••••••"
                      className="pl-11 border-sage-200 focus:border-sage-500 focus:ring-sage-500/20"
                    />
                  </div>

                  <div className="bg-sage-50 border border-sage-200 rounded-lg p-4">
                    <div className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-sage-600 mr-3 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="text-sage-800 font-medium mb-1">
                          You're creating an HR admin account for:
                        </p>
                        <p className="text-sage-700">
                          <strong>{watchedFields.companyName || 'Your Company'}</strong>
                          {watchedFields.industry && (
                            <span className="text-sage-600"> • {watchedFields.industry}</span>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={prevStep}
                      className="flex-1 border-sage-300 text-sage-700 hover:bg-sage-50"
                    >
                      Back
                    </Button>
                    <Button
                      type="submit"
                      loading={loading}
                      className="flex-1 bg-gradient-to-r from-sage-500 to-emerald-600 hover:from-sage-600 hover:to-emerald-700"
                      size="lg"
                    >
                      {loading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                  </div>
                </motion.div>
              )}
            </form>

            {/* Terms */}
            <div className="mt-6 text-center">
              <p className="text-xs text-sage-500">
                By creating an account, you agree to our{' '}
                <a href="#" className="text-sage-600 hover:underline">Terms of Service</a>
                {' '}and{' '}
                <a href="#" className="text-sage-600 hover:underline">Privacy Policy</a>
              </p>
            </div>
          </Card>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-6 flex justify-center space-x-6 text-sage-500"
          >
            <div className="flex items-center text-xs">
              <Shield className="w-4 h-4 mr-1" />
              <span>HIPAA Compliant</span>
            </div>
            <div className="flex items-center text-xs">
              <Lock className="w-4 h-4 mr-1" />
              <span>256-bit SSL</span>
            </div>
            <div className="flex items-center text-xs">
              <CheckCircle className="w-4 h-4 mr-1" />
              <span>SOC 2 Certified</span>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </div>
  )
}